import {
    type ConversationPagesMutator,
    type OrganizationConversationsResponse,
    updateConversationInPages,
} from '@apps/web/swr_data_fetching/conversation_pages';
import { type ApiClient } from '@libraries/typescript/api_client';

function patchUnreadCountLeavingUnresolved(
    pages: Array<OrganizationConversationsResponse> | undefined,
    conversationId: string,
    unreadCount: number,
): Array<OrganizationConversationsResponse> {
    // SWR's optimisticData type cannot express "leave the cache unresolved".
    return updateConversationInPages(pages, conversationId, (conversation) => ({
        ...conversation,
        unread_count: unreadCount,
    })) as Array<OrganizationConversationsResponse>;
}

export async function markConversationAsRead(
    apiClient: ApiClient,
    organizationId: string,
    conversationId: string,
    mutators: ReadonlyArray<ConversationPagesMutator>,
): Promise<void> {
    // The request cannot be owned by a cache mutation: a mutator whose SWR key
    // is null never runs its data function, so a list that is disabled until
    // its scope resolves would swallow the mark-read.
    const request: Promise<void> = apiClient
        .post({
            url: '/organizations/{organization_id}/conversations/{conversation_id}/mark-read',
            pathParams: {
                organization_id: organizationId,
                conversation_id: conversationId,
            },
        })
        .then(() => undefined);

    await Promise.all(
        mutators.map(async (mutate) => {
            let hadLoadedPages: boolean | undefined;
            try {
                await mutate(
                    async (pages) => {
                        hadLoadedPages = pages !== undefined;
                        await request;
                        return undefined;
                    },
                    {
                        optimisticData: (pages) =>
                            patchUnreadCountLeavingUnresolved(
                                pages,
                                conversationId,
                                0,
                            ),
                        rollbackOnError: true,
                        // Literal false is deliberate: useSWRInfinite treats a
                        // function as a per-page predicate, forcing mutation
                        // revalidation and rebuilding warm lists from stale per-page
                        // caches.
                        revalidate: false,
                        populateCache: false,
                    },
                );
            } finally {
                // If the data function never ran, no mutation reached the
                // cache, so there is nothing to recover.
                if (hadLoadedPages === false) {
                    // SWR discards a fetch that overlapped the mutation. We do
                    // not detect that overlap directly; recover only when the
                    // surface has no data, because that is the only case where
                    // the discard leaves the user with nothing rendered.
                    await mutate().catch((error) => {
                        console.error(
                            'Failed to reload conversations after mark-read',
                            error,
                        );
                    });
                }
            }
        }),
    );
    await request;
}
